/**
 * What the two-blocks panel should be showing, given what is in its two boxes.
 *
 * Pure, and deliberately separate from the code that touches the DOM: the
 * interesting decisions here — which findings to raise, which rows the preview
 * shows, when the counters have diverged — are the ones worth testing, and none
 * of them needs a browser to be tested in. `campaign.ts` is then thin enough to
 * read in one sitting.
 *
 * The parse itself is `parseTwoBlocks` from `src/domain/`: the same function the
 * server re-runs on submit, so the preview cannot show a pairing the server
 * would then disagree with.
 */

import { parseTwoBlocks, type TwoBlockOutcome } from '../domain/two-blocks.ts';
import { previewSlice } from './preview.ts';

export type FindingName =
  | 'one-block'
  | 'too-many-blocks'
  | 'count-mismatch'
  | 'blocks-reversed'
  | 'both-blocks-phones'
  | 'separator-in-lines';

export interface Finding {
  readonly name: FindingName;
  /** Substituted into the `{placeholders}` of the catalogue message. */
  readonly params: Readonly<Record<string, number>>;
}

export type PreviewRow =
  | { readonly kind: 'pair'; readonly line: number; readonly phone: string; readonly code: string }
  /** A line from the tail of the longer block: one side of it has no partner. */
  | {
      readonly kind: 'unpaired';
      readonly line: number;
      readonly phone?: string;
      readonly code?: string;
    }
  /** Stands in for the rows between the head and the tail of a long list. */
  | { readonly kind: 'gap'; readonly hidden: number };

export interface BlocksView {
  readonly phoneCount: number;
  readonly codeCount: number;
  /** The counters go red on this, which is the whole point of having them. */
  readonly diverged: boolean;
  readonly findings: readonly Finding[];
  readonly rows: readonly PreviewRow[];
  /** Whether a swap is worth offering, and what the text would become. */
  readonly swapped?: string;
}

/**
 * The two boxes as the one string everything downstream parses.
 *
 * The panel draws two textareas; `parseTwoBlocks`, `checkCampaign` and the gate
 * all read a single string with a blank line in it. This is the only place the
 * two are reconciled, and it is deliberately trivial — a blank line between
 * them and nothing else.
 *
 * **Nothing is trimmed.** A box pasted out of a spreadsheet ends in a newline,
 * which would make the join `…\n\n\n…`; a run of blank lines is one boundary
 * (`splitOnBlankLines`), so that costs nothing. Trimming would: every problem
 * this panel reports is reported by line number, and a leading blank line
 * somebody can count past is a line the numbers have to keep.
 */
export function joinBlocks(phones: string, codes: string): string {
  return `${phones}\n\n${codes}`;
}

export function blocksView(text: string): BlocksView {
  const outcome = parseTwoBlocks(text);
  const rows = windowed(allRows(outcome));

  const swapped = outcome.warnings.includes('blocks-reversed') ? swapBlocks(text) : undefined;

  return {
    phoneCount: outcome.phoneCount,
    codeCount: outcome.codeCount,
    // Only once there are two blocks: with one block or three, `codeCount` is
    // not a count of promo codes and saying it diverged would be noise on top
    // of the finding that actually explains the problem.
    diverged: outcome.blocks === 2 && outcome.phoneCount !== outcome.codeCount,
    findings: findingsFor(outcome),
    rows,
    ...(swapped === undefined ? {} : { swapped }),
  };
}

function findingsFor(outcome: TwoBlockOutcome): readonly Finding[] {
  const findings: Finding[] = [];

  // 'empty' is not a finding: nothing has been pasted yet, and a panel that
  // complains before it has been used is a panel nobody reads.
  if (outcome.problem === 'one-block') findings.push({ name: 'one-block', params: {} });
  if (outcome.problem === 'too-many-blocks') {
    findings.push({ name: 'too-many-blocks', params: { blocks: outcome.blocks } });
  }
  if (outcome.problem === 'count-mismatch') {
    findings.push({
      name: 'count-mismatch',
      params: { phones: outcome.phoneCount, codes: outcome.codeCount },
    });
  }

  for (const warning of outcome.warnings) findings.push({ name: warning, params: {} });
  return findings;
}

/**
 * The pairs, then the tail that has no partner. One list, so the preview's
 * "last five rows" really are the last five — which is where an unpaired tail
 * is, and therefore where someone is looking for it.
 */
function allRows(outcome: TwoBlockOutcome): readonly PreviewRow[] {
  const pairs: PreviewRow[] = outcome.pairs.map((pair) => ({
    kind: 'pair',
    line: pair.line,
    phone: pair.phone,
    code: pair.promoCode,
  }));

  const tail: PreviewRow[] = outcome.unpaired.map((line) =>
    outcome.unpairedIn === 'codes'
      ? { kind: 'unpaired', line: line.number, code: line.text }
      : { kind: 'unpaired', line: line.number, phone: line.text },
  );

  return [...pairs, ...tail];
}

function windowed(rows: readonly PreviewRow[]): readonly PreviewRow[] {
  const slice = previewSlice(rows);
  if (slice.hidden === 0) return slice.head;

  return [...slice.head, { kind: 'gap', hidden: slice.hidden }, ...slice.tail];
}

/**
 * The text with its two blocks the other way round, for the swap the reversed
 * warning offers. Rebuilt from the trimmed lines rather than by cutting the
 * original string: the blank run between the blocks may be any number of lines,
 * and the point of the button is to produce something the parser accepts.
 */
export function swapBlocks(text: string): string | undefined {
  const outcome = parseTwoBlocks(text);
  if (outcome.blocks !== 2) return undefined;

  const phones: string[] = [];
  const codes: string[] = [];
  for (const pair of outcome.pairs) {
    phones.push(pair.phone);
    codes.push(pair.promoCode);
  }
  for (const line of outcome.unpaired) {
    (outcome.unpairedIn === 'codes' ? codes : phones).push(line.text);
  }

  return `${codes.join('\n')}\n\n${phones.join('\n')}\n`;
}
